import React from 'react';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import ResponsibleForm from '@/features/responsible/components/ResponsibleForm.tsx';
import { useFormik } from 'formik';
import { ResponsibleModel } from '@/features/responsible/types';
import * as Yup from 'yup';
import { createResponsible } from '@/features/responsible/api/createResponsible.ts';
import { enqueueSnackbar } from 'notistack';
import useLoadingActions from '@/hooks/useLoadingActions.tsx';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import { QueryKey, useQueryClient } from '@tanstack/react-query';

type ResponsibleFormProps = {
  handleClose: () => void;
}

const AddResponsible: React.FC<ResponsibleFormProps> = ({ handleClose }) => {
  const { setIsLoading } = useLoadingActions();
  const { toggleCardResponsible, setResponsible, setSerieId } = useModalStore((state) => state);
  const { refetchQueries } = useQueryClient()

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: '#fff',
    borderRadius: '20px',

    maxHeight: '90%',

    overflowY: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: '#828282 #fff',
    '&:hover': {
      overflowY: 'auto',
    },
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#fff',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#828282',
      borderRadius: '8px',
    },
  };

  const formik = useFormik<ResponsibleModel>({
    initialValues: {
      name: '',
      cpf: '',
      email: '',
      address: '',
      phone: '',
      currentSchool: '',
      where_did_you_meet: '',
      turma_id: null,
      turno_id: null,
      serie_id: null,
      student: {
        nome: ''
      },
    } as unknown as ResponsibleModel,
    validationSchema: Yup.object({
      name: Yup.string().required('Campo obrigatório'),
      cpf: Yup.string().required('Campo obrigatório'),
      email: Yup.string().required('Campo obrigatório'),
      address: Yup.string().required('Campo obrigatório'),
      phone: Yup.string().required('Campo obrigatório'),
      school: Yup.string().required('Campo obrigatório'),
      where_did_you_meet: Yup.string().required('Campo obrigatório'),
      student: Yup.object({
        nome: Yup.string().required('Campo obrigatório')
      }),
      serie_id: Yup.string().required('Campo obrigatório'),
      turma_id: Yup.string().required('Campo obrigatório'),
      turno_id: Yup.string().required('Campo obrigatório'),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true, 'Cadastrando responsável...');

        const data = await createResponsible(values);

        enqueueSnackbar('Responsável cadastrada com sucesso!', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });

        setResponsible(data);
        // use the serie id returned by createResponsible (mapped as serie_id) for subsequent screens
        setSerieId((data as any).serie_id ?? (data as any).serie ?? values.serie_id);
        refetchQueries({ queryKey: 'fetch-columns' as unknown as QueryKey }).then()
        refetchQueries({ queryKey: 'step-columns' as unknown as QueryKey }).then()

        handleClose();
      } catch (e) {
        enqueueSnackbar((e as Error).message || 'Falha ao cadastrar responsável!', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setIsLoading(false, '');
      }
    },
  });

  return (
    <Modal
      open={true}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Box style={{ background: '#93DC3F', borderRadius: '20px 20px 0 0', boxShadow: '0px 4px 4px 0px #00000040' }}>
          <Typography color={'#231F20'} variant="h1" p={'10px 29px'}>
            Dados do responsável e aluno
          </Typography>
        </Box>
        <Stack component={'form'}
               sx={{ p: '20px 30px 50px 30px' }}
               noValidate
               onSubmit={formik.handleSubmit}
               justifyContent={'center'}>
          <ResponsibleForm formik={formik} />
          <Stack margin={'auto'} gap={'20px'} flexDirection={'row'}>
            <Button
              sx={{ width: '164px', borderRadius: '24px', fontSize: '14px', mt: '40px', bgcolor: '#D63429' }}
              variant="contained"
              onClick={handleClose}
            >
              Descartar
            </Button>
            <Button
              sx={{ width: '164px', borderRadius: '24px', fontSize: '14px', mt: '40px', bgcolor: '#15AB51' }}
              variant="contained"
              type={"submit"}
            >
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AddResponsible;
